import React from 'react'
import { useForm } from '@mantine/form'
import { Button, Paper, Grid, TextInput, Radio, Group, Stack, Alert } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from 'hooks/useTranslation'
import { IconAlertCircle } from '@tabler/icons-react'

import { SelectClearable } from 'ui/SelectClearable'
import { queryAdvertisers } from 'supabase/advertisers'
import {
  CampaignEditValues,
  CampaignWithAdvertiser,
  LanguageTarget,
  PlatformTarget
} from 'supabase/campaigns/types'
import { LANGUAGE_TARGET_OPTIONS, PLATFORM_TARGET_OPTIONS } from 'supabase/campaigns/targeting'

export interface EditCampaignFormProps {
  campaign: CampaignWithAdvertiser
  onSubmit: (data: CampaignEditValues) => void
  loading?: boolean
}

export function EditCampaignForm({ campaign, onSubmit, loading = false }: EditCampaignFormProps) {
  const { t } = useAppTranslation()

  const form = useForm<CampaignEditValues>({
    initialValues: {
      name: campaign.name,
      advertiser_id: campaign.advertiser_id,
      tier: campaign.tier,
      // The DB check constraints only allow these values; the Row type says string
      language_target: campaign.language_target as LanguageTarget,
      platform_target: campaign.platform_target as PlatformTarget,
      start_date: campaign.start_date,
      end_date: campaign.end_date
    },
    validate: {
      name: (value) => {
        if (!value) return 'Campaign name is required'
        if (value.length < 2) return 'Campaign name should have at least 2 characters'
        return null
      },
      advertiser_id: (value) => (!value ? 'Advertiser is required' : null),
      tier: (value) => (!value ? 'Tier is required' : null),
      start_date: (value) => (!value ? 'Start date is required' : null),
      end_date: (value, values) => {
        if (!value) return 'End date is required'
        if (values.start_date && new Date(value) < new Date(values.start_date)) {
          return 'End date must be after start date'
        }
        return null
      }
    }
  })

  // Fetch advertisers for dropdown
  const { data: advertisersData, isLoading: advertisersLoading } = useQuery({
    queryKey: ['advertisers'],
    queryFn: () => queryAdvertisers()
  })

  const advertiserOptions = advertisersData?.advertisers.map(advertiser => ({
    value: advertiser.id,
    label: `${advertiser.name} (${advertiser.category})`
  })) || []

  const handleSubmit = (values: CampaignEditValues) => {
    // Only include changed fields in the update
    const changedFields: CampaignEditValues = {}

    // Check each field individually for changes
    if (values.name && values.name !== campaign.name) {
      changedFields.name = values.name
    }
    if (values.advertiser_id && values.advertiser_id !== campaign.advertiser_id) {
      changedFields.advertiser_id = values.advertiser_id
    }
    if (values.tier && values.tier !== campaign.tier) {
      changedFields.tier = values.tier
    }
    // Targeting is editable in any status (spec D11); the DB trigger logs the change
    if (values.language_target && values.language_target !== campaign.language_target) {
      changedFields.language_target = values.language_target
    }
    if (values.platform_target && values.platform_target !== campaign.platform_target) {
      changedFields.platform_target = values.platform_target
    }
    if (values.start_date && values.start_date !== campaign.start_date) {
      changedFields.start_date = values.start_date
    }
    if (values.end_date && values.end_date !== campaign.end_date) {
      changedFields.end_date = values.end_date
    }

    if (Object.keys(changedFields).length > 0) {
      onSubmit(changedFields)
    }
  }

  // Show warning if editing active campaign
  const isActiveCampaign = campaign.status === 'Active'

  return (
    <Paper p="md">
      {isActiveCampaign && (
        <Alert icon={<IconAlertCircle size="1rem" />} color="yellow" mb="md">
          {t('campaigns.edit_active_warning') || 'You are editing an active campaign. Changes will affect live ads immediately.'}
        </Alert>
      )}

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Grid>
            <Grid.Col span={12}>
              <TextInput
                label={t('campaigns.name') as string}
                placeholder={t('campaigns.name_placeholder') as string}
                required
                disabled={campaign.status === 'Ended'}
                {...form.getInputProps('name')}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <div>
                <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  {t('campaigns.advertiser')} *
                </label>
                <SelectClearable
                  placeholder={t('campaigns.select_advertiser') as string}
                  data={advertiserOptions}
                  value={form.values.advertiser_id}
                  onChange={(value) => form.setFieldValue('advertiser_id', value || '')}
                  loading={advertisersLoading}
                  disabled={campaign.status !== 'Draft'}
                  emptyMessage={t('campaigns.no_advertisers_found') as string}
                />
                {form.errors.advertiser_id && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {form.errors.advertiser_id}
                  </div>
                )}
              </div>
            </Grid.Col>

            <Grid.Col span={6}>
              <Radio.Group
                label={t('campaigns.tier') as string}
                required
                {...form.getInputProps('tier')}
              >
                <Group mt="xs">
                  <Radio value="Premium" label={t('campaigns.tier_premium') as string} disabled={campaign.status !== 'Draft'} />
                  <Radio value="Standard" label={t('campaigns.tier_standard') as string} disabled={campaign.status !== 'Draft'} />
                  <Radio value="Starter" label={t('campaigns.tier_starter') as string} disabled={campaign.status !== 'Draft'} />
                </Group>
              </Radio.Group>
            </Grid.Col>

            <Grid.Col span={6}>
              <Radio.Group
                label={t('campaigns.language_target') as string}
                required
                {...form.getInputProps('language_target')}
              >
                <Group mt="xs">
                  {LANGUAGE_TARGET_OPTIONS.map((option) => (
                    <Radio key={option.value} value={option.value} label={t(option.labelKey) as string} />
                  ))}
                </Group>
              </Radio.Group>
            </Grid.Col>

            <Grid.Col span={6}>
              <Radio.Group
                label={t('campaigns.platform_target') as string}
                required
                {...form.getInputProps('platform_target')}
              >
                <Group mt="xs">
                  {PLATFORM_TARGET_OPTIONS.map((option) => (
                    <Radio key={option.value} value={option.value} label={t(option.labelKey) as string} />
                  ))}
                </Group>
              </Radio.Group>
            </Grid.Col>

            <Grid.Col span={6}>
              <DatePickerInput
                label={t('campaigns.start_date') as string}
                placeholder={t('campaigns.start_date_placeholder') as string}
                value={form.values.start_date ? new Date(form.values.start_date) : null}
                onChange={(date) => form.setFieldValue('start_date', date ? date.toISOString().split('T')[0] : '')}
                disabled={campaign.status === 'Ended' || campaign.status === 'Active'}
                required
                error={form.errors.start_date}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <DatePickerInput
                label={t('campaigns.end_date') as string}
                placeholder={t('campaigns.end_date_placeholder') as string}
                value={form.values.end_date ? new Date(form.values.end_date) : null}
                onChange={(date) => form.setFieldValue('end_date', date ? date.toISOString().split('T')[0] : '')}
                disabled={campaign.status === 'Ended'}
                required
                error={form.errors.end_date}
              />
            </Grid.Col>
          </Grid>

          <Group justify="flex-end" mt="md">
            <Button type="submit" loading={loading}>
              {t('common.save_changes') as string}
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  )
}